import ReactGA from "react-ga4";

const CONSENT_KEY = "cookieConsent";
const JOURNEY_ID_KEY = "onegradJourneyId";
const ATTRIBUTION_KEY = "onegradAttribution";
const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "og_source",
  "og_entry_path",
  "cta_location",
  "cta_label",
];
const JOURNEY_EVENTS = new Set([
  "registration_started",
  "authentication_succeeded",
  "authentication_failed",
  "auth_contract_failed",
  "generation_started",
  "generation_succeeded",
  "generation_failed",
  "generation_retry",
  "duplicate_suppressed",
  "persistence_failed",
  "navigation",
]);
const JOURNEY_FIELDS = new Set([
  "correlation_id",
  "job_id",
  "attempt_count",
  "duration_ms",
  "error_category",
  "terminal_status",
  "replayed",
  "destination",
  "source",
]);

const isBrowser = () => typeof window !== "undefined";

export const hasAnalyticsConsent = () =>
  isBrowser() && localStorage.getItem(CONSENT_KEY) === "true";

export const getJourneyId = () => {
  if (!isBrowser()) return "";

  const existing = localStorage.getItem(JOURNEY_ID_KEY);
  if (existing) return existing;

  const generated =
    typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  localStorage.setItem(JOURNEY_ID_KEY, generated);
  return generated;
};

const getAttribution = () => {
  if (!isBrowser()) return {};

  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || "{}");
  } catch {
    return {};
  }
};

export const persistAnalyticsAttribution = (search = window.location.search) => {
  if (!isBrowser()) return;

  const params = new URLSearchParams(search);
  const nextAttribution = { ...getAttribution() };

  const journeyId = params.get("og_journey_id");
  if (journeyId) {
    localStorage.setItem(JOURNEY_ID_KEY, journeyId);
  } else {
    getJourneyId();
  }

  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) nextAttribution[key] = value;
  });

  if (Object.keys(nextAttribution).length) {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(nextAttribution));
  }
};

export const getAnalyticsContext = () => ({
  journey_id: getJourneyId(),
  app_surface: "react_app",
  page_path: isBrowser() ? window.location.pathname : "",
  ...getAttribution(),
});

export const trackFunnelEvent = (eventName, params = {}) => {
  if (!hasAnalyticsConsent() || !ReactGA.isInitialized) return;

  ReactGA.event(eventName, {
    ...getAnalyticsContext(),
    ...params,
  });
};

export const sanitizeJourneyProperties = (params = {}) =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([key, value]) => JOURNEY_FIELDS.has(key) && value != null)
      .map(([key, value]) => [
        key,
        typeof value === "string" ? value.slice(0, 160) : value,
      ])
  );

export const trackJourneyEvent = (eventName, params = {}) => {
  if (!JOURNEY_EVENTS.has(eventName)) {
    throw new Error(`Unsupported journey event: ${eventName}`);
  }
  trackFunnelEvent(eventName, sanitizeJourneyProperties(params));
};

export const trackFunnelPageView = (pagePath, params = {}) => {
  if (!hasAnalyticsConsent() || !ReactGA.isInitialized) return;

  ReactGA.send({
    hitType: "pageview",
    page: pagePath,
    ...getAnalyticsContext(),
    ...params,
  });
};
